var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as THREE from "../../src/insole-web-common/vendor/three/build/three.module.js";
import { defaultExportParameters, defaultMeshGenerationParameters, mm2cm, } from "../insole-web-common/src/engine/parameters.js";
import { UpdateGeometryFromMeshData } from "../insole-web-common/src/mesh-utils.js";
import { OrbitControls } from "../../src/insole-web-common/vendor/three/examples/jsm/controls/OrbitControls.js";
import { ConvertHPMetadata } from "../metadata/metadata-hp.js";
import { ApplyMetadata, ExportMetadata } from "../metadata/metadata.js";
import { defaultCustomInsoleParameters } from "../parameter-defaults.js";
import { Float3ToVector3, IsNotNullish, IsNullish, Vector4ToFloat4 } from "../util.js";
import { ConvertPrescriptionToMetadata } from "./adapter-client/adapter-client.js";
import { ImportFootScanMesh } from "./common.js";
import { insoleInspectionUIHTML } from "./insole-inspection-ui.js";
import { DeepClone } from "../insole-web-common/src/util.js";
import { EventDispatcher } from "../event-dispatcher.js";
import { FootscanMetadataBackwardCompatibilityConversion, GetCurrentMetadataVersion, MetadataBackwardCompatibilityConversion, } from "../metadata/metadata-backwards-compatibility.js";
const defaultExportParametersForInsoleInspection = Object.assign(Object.assign({}, defaultExportParameters), { hasPlantarPattern: true, embossedLogo: true, numVertices: { x: 128, y: 64, z: 24 } });
const initialCameraPosition = new THREE.Vector3(-20, 10, 15);
const initialCameraTarget = new THREE.Vector3(0, -3, 0);
const autoRotateSpeed = -4;
const maxCameraDistance = 50;
const insoleSpacing = 5.0;
const footScanMaterial = new THREE.MeshPhongMaterial({ color: 0xf0f0f0, side: THREE.DoubleSide });
const insoleMaterial = new THREE.MeshPhongMaterial({ color: 0xbcccdf, shininess: 0 });
const engineActivationInterval = 2 * 60 * 1000; // 2 minutes, in milliseconds
export class InsoleInspectionScene {
    static create(visualizer, renderer, initData, uiRoot) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const leoMetadata = (() => {
                switch (initData.insoleParameters.type) {
                    case "prescription": {
                        // Converting to HP metadata, then to leo metadata
                        const hasLeftFoot = initData.leftScanData !== null;
                        const hasRightFoot = initData.rightScanData !== null;
                        if (!hasLeftFoot && !hasRightFoot) {
                            throw Error("No foot scans were specified");
                        }
                        const converted = ConvertPrescriptionToMetadata(initData.insoleParameters.prescription, null, null);
                        return ConvertHPMetadata({
                            leftFootMetadata: hasLeftFoot ? converted.leftFootMetadata : null,
                            rightFootMetadata: hasRightFoot ? converted.rightFootMetadata : null,
                        });
                    }
                    case "metadata-hp": {
                        return ConvertHPMetadata(initData.insoleParameters.metadata);
                    }
                    case "metadata-leo": {
                        // Already in correct format
                        MetadataBackwardCompatibilityConversion(initData.insoleParameters.metadata.left, null);
                        MetadataBackwardCompatibilityConversion(initData.insoleParameters.metadata.right, null);
                        return initData.insoleParameters.metadata;
                    }
                    default:
                        unreachable(initData.insoleParameters);
                }
            })();
            function ImportSide(metadata, footScanData, side) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (metadata === null && footScanData === null) {
                        return null;
                    }
                    const engine = yield visualizer.acquireEngine(side);
                    yield engine.SetDefaultInsoleParameters();
                    const allTemplateCategories = 1 /* TemplateCategoryMask.HPUS */ |
                        2 /* TemplateCategoryMask.HPEU */ |
                        4 /* TemplateCategoryMask.LeoUS */ |
                        8 /* TemplateCategoryMask.LeoEU */;
                    yield engine.SetEnabledTemplateCategories(allTemplateCategories);
                    const activationResult = yield visualizer.ActivateEngine(engine);
                    if (activationResult !== 0 /* EngineActivationResult.Success */) {
                        return null;
                    }
                    let insoleParameterCollection = Object.assign({ insoleParams: yield engine.GetDefaultInsoleParameters(), meshGenParams: defaultMeshGenerationParameters, exportParams: defaultExportParametersForInsoleInspection }, defaultCustomInsoleParameters);
                    const footScanImportData = yield (() => __awaiter(this, void 0, void 0, function* () {
                        var _a, _b;
                        if (footScanData === null) {
                            return null;
                        }
                        const importResult = yield ImportFootScanMesh(engine, footScanData);
                        if (importResult === null || importResult.meshData === null) {
                            return null;
                        }
                        const initialAlignedPose = yield engine.SetFootScan({
                            alignWithInsole: true,
                            meshPtr: importResult.ptr,
                            scanType: (_a = footScanData.scanMode) !== null && _a !== void 0 ? _a : 0 /* FootScanType.SemiWeighted */,
                            side,
                        });
                        if (initialAlignedPose === null) {
                            return null;
                        }
                        if ((_b = metadata === null || metadata === void 0 ? void 0 : metadata.footScanParameters) === null || _b === void 0 ? void 0 : _b.landmarkPositions) {
                            if (IsNotNullish(importResult.originalBBoxCenter)) {
                                FootscanMetadataBackwardCompatibilityConversion(metadata, importResult.originalBBoxCenter.y);
                            }
                            const { mpj1, mpj5, arch, boh, lh, mh } = metadata.footScanParameters.landmarkPositions;
                            const landmarksToUpdate = [];
                            const UpdateLandmarkPosition = (name, position) => {
                                if (!IsNullish(position)) {
                                    landmarksToUpdate.push({ name, position });
                                }
                            };
                            UpdateLandmarkPosition(0 /* FootscanLandmarkName.Mpj1 */, mpj1);
                            UpdateLandmarkPosition(2 /* FootscanLandmarkName.Mpj5 */, mpj5);
                            UpdateLandmarkPosition(10 /* FootscanLandmarkName.Arch */, arch);
                            UpdateLandmarkPosition(6 /* FootscanLandmarkName.Boh */, boh);
                            UpdateLandmarkPosition(9 /* FootscanLandmarkName.Lh */, lh);
                            UpdateLandmarkPosition(8 /* FootscanLandmarkName.Mh */, mh);
                            yield engine.SetFootscanLandmark(...landmarksToUpdate);
                            yield engine.FinalizeLandmarks(true);
                        }
                        const bestFitInsoleData = yield engine.SelectBestFitInsole({});
                        if (bestFitInsoleData === null) {
                            return null;
                        }
                        // Get template, sizes, arch height, etc.
                        insoleParameterCollection.insoleParams = yield engine.GetInsoleParameters();
                        const footScanGeometry = new THREE.BufferGeometry();
                        UpdateGeometryFromMeshData(footScanGeometry, importResult.meshData);
                        const footScanObject = new THREE.Mesh(footScanGeometry, footScanMaterial);
                        footScanObject.frustumCulled = false;
                        return {
                            geometry: footScanGeometry,
                            obj: footScanObject,
                            alignedPose: bestFitInsoleData.alignedPose,
                        };
                    }))();
                    const initialInsoleParametersCollection = DeepClone(insoleParameterCollection);
                    if (metadata !== null) {
                        const insoleParamsFromMetadata = yield ApplyMetadata(metadata, insoleParameterCollection, true);
                        if (insoleParamsFromMetadata !== null) {
                            insoleParameterCollection = insoleParamsFromMetadata;
                        }
                    }
                    metadata = ExportMetadata(insoleParameterCollection, true);
                    engine.SetTemplate(insoleParameterCollection.selectedTemplate);
                    const insoleObject = new THREE.Group();
                    insoleObject.frustumCulled = false;
                    const fastExportParams = DeepClone(initialInsoleParametersCollection.exportParams);
                    fastExportParams.hasPlantarPattern = false;
                    fastExportParams.embossedLogo = false;
                    fastExportParams.embossedText = "";
                    const exportedGeometry = new THREE.BufferGeometry();
                    const exportedInsoleObj = new THREE.Mesh(exportedGeometry, insoleMaterial);
                    insoleObject.add(exportedInsoleObj);
                    const sideData = {
                        insole: {
                            obj: insoleObject,
                            previewGeometries: [],
                            meshVersion: 0,
                            exportedGeometry,
                            exportedInsoleObj,
                            bbox: new THREE.Box3(new THREE.Vector3(), new THREE.Vector3()),
                            exportParamsForGenerate: fastExportParams,
                        },
                        footScan: footScanImportData,
                        metadata: { version: GetCurrentMetadataVersion() }, // Updated later
                        requestRender: new EventDispatcher(),
                        updateInsolePositioning: new EventDispatcher(),
                        engineActivationIntervalId: setInterval(() => __awaiter(this, void 0, void 0, function* () { return visualizer.ActivateEngine(yield visualizer.acquireEngine(side)); }), engineActivationInterval),
                        disposed: false,
                        applyMetadata: (newMetadata) => __awaiter(this, void 0, void 0, function* () {
                            // First, generate insole without export, then generate it again with export
                            // Also disable tools that are slow
                            const meshVersion = ++sideData.insole.meshVersion;
                            const isLatestVersion = () => meshVersion === sideData.insole.meshVersion;
                            sideData.metadata = newMetadata;
                            const insoleParamsFromMetadata = yield ApplyMetadata(newMetadata, initialInsoleParametersCollection, true);
                            if (insoleParamsFromMetadata === null || !isLatestVersion()) {
                                return false;
                            }
                            sideData.metadata = ExportMetadata(insoleParamsFromMetadata, true);
                            const insoleGenerationResult = yield engine.GenerateInsole(insoleParamsFromMetadata.insoleParams, fastExportParams, insoleParamsFromMetadata.meshGenParams, true, // alignFootScan
                            false, // calculateMeasurements
                            false);
                            if (insoleGenerationResult === null ||
                                insoleGenerationResult.isExport ||
                                insoleGenerationResult.result.meshes === null ||
                                !isLatestVersion()) {
                                return false;
                            }
                            const insoleBoundingBox = yield engine.MeshCalculateBoundingBox(insoleGenerationResult.result.meshes.map(meshData => meshData.nativePtr));
                            sideData.insole.bbox.set(Float3ToVector3(insoleBoundingBox.minP), Float3ToVector3(insoleBoundingBox.maxP));
                            sideData.updateInsolePositioning.dispatchEvent();
                            // Create preview meshes/objects if needed
                            const previewGeometries = sideData.insole.previewGeometries;
                            while (previewGeometries.length < insoleGenerationResult.result.meshes.length) {
                                const geometry = new THREE.BufferGeometry();
                                previewGeometries.push(geometry);
                                insoleObject.add(new THREE.Mesh(geometry, insoleMaterial));
                            }
                            for (let i = 0; i < insoleGenerationResult.result.meshes.length; ++i) {
                                const generatedMesh = insoleGenerationResult.result.meshes[i];
                                UpdateGeometryFromMeshData(previewGeometries[i], generatedMesh);
                            }
                            // Hide exported insole (will show it again after the export is done)
                            exportedInsoleObj.visible = false;
                            sideData.requestRender.dispatchEvent();
                            if (!isLatestVersion()) {
                                return false;
                            }
                            // Start exporting, but don't await it
                            (() => __awaiter(this, void 0, void 0, function* () {
                                // Make sure that engine is built with ENABLER_SERVICE_EXPORT disabled.
                                // When it's enabled the engine will fail exporting this way (only available via EnablerServiceBeginExport, EnablerServiceExportInsole and EnablerServiceEndExport).
                                const insoleGenerationResult = yield engine.GenerateInsole(insoleParamsFromMetadata.insoleParams, insoleParamsFromMetadata.exportParams, insoleParamsFromMetadata.meshGenParams, true, // alignFootScan
                                false, // calculateMeasurements
                                true);
                                if (insoleGenerationResult === null ||
                                    !insoleGenerationResult.isExport ||
                                    insoleGenerationResult.exportedMesh === null ||
                                    !isLatestVersion()) {
                                    return;
                                }
                                const meshPtr = insoleGenerationResult.exportedMesh.nativePtr;
                                // Exported insoles are in a different orientation
                                const exportRotationInverse = Vector4ToFloat4(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 2));
                                yield engine.MeshTransform(meshPtr, false, undefined, exportRotationInverse, mm2cm);
                                if (!isLatestVersion()) {
                                    return;
                                }
                                const newMeshData = yield engine.MeshGenerateNormals(meshPtr, true, {
                                    mode: "withAngleThreshold",
                                    angleThresholdDegrees: 60,
                                });
                                if (newMeshData === null || sideData.disposed || !isLatestVersion()) {
                                    return;
                                }
                                UpdateGeometryFromMeshData(sideData.insole.exportedGeometry, newMeshData);
                                visualizer.releaseEngine(side);
                                // Hide preview meshes
                                for (const previewGeometry of previewGeometries) {
                                    previewGeometry.setDrawRange(0, 0);
                                }
                                exportedInsoleObj.visible = true;
                                sideData.requestRender.dispatchEvent();
                            }))();
                            return true;
                        }),
                    };
                    yield sideData.applyMetadata(metadata);
                    return sideData;
                });
            }
            const [leftData, rightData] = yield Promise.all([
                ImportSide((_a = leoMetadata.left) !== null && _a !== void 0 ? _a : null, initData.leftScanData, 0 /* FootSide.Left */),
                ImportSide((_b = leoMetadata.right) !== null && _b !== void 0 ? _b : null, initData.rightScanData, 1 /* FootSide.Right */),
            ]);
            if (leftData === null && rightData === null) {
                return null;
            }
            return new InsoleInspectionScene(renderer, leftData, rightData, uiRoot);
        });
    }
    constructor(renderer, leftData, rightData, uiRoot) {
        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);
        this.renderRequested = false;
        this.disposed = false;
        this.renderer = renderer;
        this.leftData = leftData;
        this.rightData = rightData;
        const addObjectsToScene = (data) => {
            if (data === null) {
                return;
            }
            this.scene.add(data.insole.obj);
            if (data.footScan !== null) {
                // TODO?: should the foot scan be visible?
                // this.scene.add(data.footScan.obj);
            }
            data.requestRender.addListener(() => this.render());
            data.updateInsolePositioning.addListener(() => this.updateInsolePositioning());
        };
        addObjectsToScene(this.leftData);
        addObjectsToScene(this.rightData);
        this.camera.position.copy(initialCameraPosition);
        this.scene.add(this.camera);
        this.light = new THREE.DirectionalLight(0xffffff, 0.55);
        this.light.add(this.light.target);
        this.light.target.position.set(0, 0, -1);
        this.camera.add(this.light);
        this.scene.add(new THREE.AmbientLight(0xffffff, 0.45));
        this.controls = new OrbitControls(this.camera, renderer.domElement);
        this.controls.maxDistance = maxCameraDistance;
        this.controls.autoRotate = false;
        this.controls.autoRotateSpeed = autoRotateSpeed;
        this.controls.target.copy(initialCameraTarget);
        this.controls.mouseButtons.LEFT = -1; // Do nothing on left click
        this.controls.mouseButtons.MIDDLE = THREE.MOUSE.PAN;
        this.controls.mouseButtons.RIGHT = THREE.MOUSE.ROTATE;
        this.controls.addEventListener("change", () => this.render());
        this.controls.update();
        this.updateInsolePositioning();
        this.render();
        this.uiOverlay = document.createElement("div");
        uiRoot.appendChild(this.uiOverlay);
        this.createUI();
    }
    setSize(width, height) {
        this.renderer.setSize(width, height);
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.render(true);
    }
    render(immediately = false) {
        if (this.disposed) {
            return;
        }
        if (immediately) {
            this.light.position.copy(this.camera.position);
            this.renderer.render(this.scene, this.camera);
            return;
        }
        if (this.renderRequested) {
            return;
        }
        this.renderRequested = true;
        window.requestAnimationFrame(() => {
            this.light.position.copy(this.camera.position);
            this.renderer.render(this.scene, this.camera);
            this.renderRequested = false;
            if (this.controls.autoRotate) {
                this.controls.update();
                this.render();
            }
        });
    }
    getMetadata() {
        var _a, _b, _c, _d;
        this.throwIfDisposed();
        return {
            left: DeepClone((_b = (_a = this.leftData) === null || _a === void 0 ? void 0 : _a.metadata) !== null && _b !== void 0 ? _b : null),
            right: DeepClone((_d = (_c = this.rightData) === null || _c === void 0 ? void 0 : _c.metadata) !== null && _d !== void 0 ? _d : null),
        };
    }
    applyMetadata(metadata) {
        return __awaiter(this, void 0, void 0, function* () {
            const promises = [];
            if (IsNotNullish(metadata.left) && IsNotNullish(this.leftData)) {
                promises.push(this.leftData.applyMetadata(metadata.left));
            }
            if (IsNotNullish(metadata.right) && IsNotNullish(this.rightData)) {
                promises.push(this.rightData.applyMetadata(metadata.right));
            }
            yield Promise.all(promises);
        });
    }
    dispose() {
        this.throwIfDisposed();
        function DisposeData(data) {
            var _a;
            if (data === null) {
                return;
            }
            data.disposed = true;
            for (const previewGeometry of data.insole.previewGeometries) {
                previewGeometry.dispose();
            }
            data.insole.previewGeometries.length = 0;
            (_a = data.footScan) === null || _a === void 0 ? void 0 : _a.geometry.dispose();
            data.insole.exportedGeometry.dispose();
            clearInterval(data.engineActivationIntervalId);
        }
        DisposeData(this.leftData);
        DisposeData(this.rightData);
        this.uiOverlay.remove();
        this.controls.autoRotate = false;
        this.controls.dispose();
        this.disposed = true;
    }
    updateInsolePositioning() {
        var _a, _b;
        function AlignFootScanWithInsole(data) {
            if (data === null || data.footScan === null) {
                return;
            }
            const { position, rotation } = data.footScan.alignedPose;
            data.footScan.obj.position.copy(data.insole.obj.position).add(Float3ToVector3(position));
            data.footScan.obj.quaternion.set(rotation.x, rotation.y, rotation.z, rotation.w); // Insole rotation is identity, so no need to multiply
            data.footScan.obj.visible = data.insole.obj.visible;
        }
        if (this.leftData !== null &&
            this.rightData !== null &&
            this.leftData.insole.obj.visible &&
            this.rightData.insole.obj.visible) {
            const halfSpacing = insoleSpacing * 0.5;
            const tempVec3 = new THREE.Vector3();
            const leftPositionZ = this.leftData.insole.bbox.getSize(tempVec3).z * 0.5 + halfSpacing;
            const rightpositionZ = -this.rightData.insole.bbox.getSize(tempVec3).z * 0.5 - halfSpacing;
            this.leftData.insole.obj.position.set(0, 0, leftPositionZ);
            this.rightData.insole.obj.position.set(0, 0, rightpositionZ);
        }
        else {
            (_a = this.leftData) === null || _a === void 0 ? void 0 : _a.insole.obj.position.setScalar(0);
            (_b = this.rightData) === null || _b === void 0 ? void 0 : _b.insole.obj.position.setScalar(0);
        }
        AlignFootScanWithInsole(this.leftData);
        AlignFootScanWithInsole(this.rightData);
    }
    createUI() {
        const { style: overlayStyle } = this.uiOverlay;
        overlayStyle.position = "absolute";
        overlayStyle.bottom = "0px";
        overlayStyle.left = "0px";
        overlayStyle.width = "100%";
        overlayStyle.pointerEvents = "none";
        const shadowRoot = this.uiOverlay.attachShadow({ mode: "open" });
        shadowRoot.innerHTML = insoleInspectionUIHTML;
        const rotateCheckbox = shadowRoot.getElementById("rotate-checkbox");
        const leftToggle = shadowRoot.getElementById("show-left");
        const leftContainer = shadowRoot.getElementById("left-container");
        const rightToggle = shadowRoot.getElementById("show-right");
        const rightContainer = shadowRoot.getElementById("right-container");
        rotateCheckbox.addEventListener("input", () => this.setAutoRotate(rotateCheckbox.checked));
        const SetupToggle = (toggle, toggleContainer, data) => {
            if (data === null) {
                toggle.disabled = true;
                toggleContainer.classList.add("onoffswitch-label-disabled");
                return;
            }
            toggle.checked = true;
            toggle.addEventListener("input", () => {
                const { checked } = toggle;
                data.insole.obj.visible = checked;
                this.updateInsolePositioning();
                this.render();
            });
        };
        SetupToggle(leftToggle, leftContainer, this.leftData);
        SetupToggle(rightToggle, rightContainer, this.rightData);
    }
    setAutoRotate(enabled) {
        if (enabled) {
            this.controls.target.copy(initialCameraTarget);
            const distance = initialCameraPosition.distanceTo(initialCameraTarget);
            this.controls.minDistance = distance;
            this.controls.maxDistance = distance;
            const angle = Math.asin(Math.hypot(initialCameraPosition.x, initialCameraPosition.z) / distance);
            this.controls.minPolarAngle = angle;
            this.controls.maxPolarAngle = angle;
        }
        else {
            this.controls.minDistance = 0;
            this.controls.maxDistance = maxCameraDistance;
            this.controls.minPolarAngle = 0;
            this.controls.maxPolarAngle = Math.PI;
        }
        this.controls.autoRotate = enabled;
        this.controls.enablePan = !enabled;
        this.render();
    }
    throwIfDisposed() {
        if (this.disposed) {
            throw Error("Insole inspection scene was disposed");
        }
    }
}
//# sourceMappingURL=insole-inspection-scene.js.map